<template>
  <main class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-28">
    <section class="mx-auto max-w-4xl">
      <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <Breadcrumb :crumbs="breadcrumbItems" />
          <h2 class="text-gray-900 text-2xl font-semibold mt-2">
            {{ course.title }}
          </h2>
          <p class="text-sm text-gray-600 mt-1 flex items-center gap-2">
            <BookOpenIcon class="h-4 w-4 text-primary" />
            Modules and learning progress
          </p>
        </div>
        <div class="bg-primary/10 border border-primary/30 rounded-2xl shadow-sm px-6 py-4 w-full md:w-72">
          <div class="flex items-center justify-between">
            <div class="text-sm font-medium text-gray-600">
              Progress
            </div>
            <div class="text-3xl font-semibold text-gray-900">
              {{ computeCourseProgress(course) }}%
            </div>
          </div>
          <div
            :aria-valuenow="computeCourseProgress(course)"
            aria-valuemax="100"
            aria-valuemin="0"
            class="mt-3 flex w-full h-2 rounded-full overflow-hidden bg-gray-200"
            role="progressbar"
          >
            <div
              :style="{ width: computeCourseProgress(course) + '%' }"
              class="flex flex-col justify-center overflow-hidden bg-primary text-xs text-white text-center whitespace-nowrap transition duration-500"
            />
          </div>
        </div>
      </div>
      <div class="mt-6 mb-3 text-sm font-medium text-gray-700 flex items-center gap-2">
        <Squares2X2Icon class="h-4 w-4 text-primary" />
        Modules
      </div>
      <div
        v-for="(module, index) in course.modules"
        :key="index"
      >
        <Accordion
          :is-open="module.show"
          :progress="computeModuleProgress(module)"
          @update:is-open="module.show = $event"
        >
          <!-- Title slot -->
          <template #title>
            {{ module.title }}
          </template>
          <!-- Content count slot -->
          <template #content-count>
            {{ getContentCount(module) }}
          </template>
          <!-- Progress label and percent slots -->
          <template #progress-label>
            Progress
          </template>
          <template #progress-percent>
            {{ computeModuleProgress(module) }}% Complete
          </template>
          <!-- Module content slot (You can add content dynamically) -->
          <template #default>
            <div class="space-y-3">
              <div
                v-for="(content, contentIndex) in module.contents"
                :key="contentIndex"
                class="content-item flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2"
              >
                <div class="flex items-center">
                  <div class="flex items-center mr-3">
                    <input
                      :id="'checkbox-' + module._id + '-' + contentIndex"
                      :checked="content.completed"
                      class="w-4 h-4 rounded-full border border-gray-300 bg-white text-primary appearance-none checked:bg-primary checked:border-primary focus:ring-2 focus:ring-primary"
                      type="checkbox"
                      @change="onContentComplete(module._id, content._id)"
                    >
                  </div>
                  <div
                    class="relative mr-3 inline-flex items-center justify-center w-9 h-9 overflow-hidden rounded-full bg-[#f6f1ec] border border-gray-200"
                  >
                    <PlayCircleIcon class="h-5 w-5 text-gray-700" />
                  </div>
                  <router-link
                    :to="'/dashboard/course/' + course._id + '/' + module._id + '/' + content._id"
                    class="text-gray-900 text-sm font-medium"
                  >
                    {{ content.title }}
                  </router-link>
                </div>
                <span class="text-xs text-gray-500">{{ content.length }} min</span>
              </div>
            </div>
          </template>
        </Accordion>
      </div>
    </section>
  </main>
</template>
<script lang="ts">
import {defineComponent, inject, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {useRouter} from 'vue-router';
import Accordion from '@/components/Accordion.vue';
import Breadcrumb from '@/components/Breadcrumb.vue';
import {BreadcrumbItem, Course, Module} from '@/types';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import {BookOpenIcon, PlayCircleIcon, Squares2X2Icon} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: "Course",
  components: {
    Breadcrumb,
    Accordion,
    BookOpenIcon,
    PlayCircleIcon,
    Squares2X2Icon,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const {settings} = inject(settingsKey)!;
    const course = ref<Course>({
      _id: '',
      title: '',
      description: '',
      modules: [],
      createdAt: '',
    });
    const breadcrumbItems = ref<BreadcrumbItem[]>([
      {text: 'Dashboard', href: '/dashboard'},
      {text: 'Academy', href: '/academy', isDivider: true},
    ]);
    const loading = ref<boolean>(false);
    const error = ref<string | null>(null);

    const toggleModule = (module: Module): void => {
      module.show = !module.show;
    };

    const getContentCount = (module: Module): string => {
      let videoCount = 0;
      let documentCount = 0;
      let quizCount = 0;

      for (const content of module.contents) {
        if (!Object.prototype.hasOwnProperty.call(content, 'type')) {
          console.error(`Content object doesn't have 'type' property: `, content);
          continue;
        }
        switch (content.type) {
          case 'video':
            videoCount++;
            break;
          case 'article':
            documentCount++;
            break;
          case 'quiz':
            quizCount++;
            break;
          default:
            console.error(`Unknown content type: ${content.type}`);
        }
      }

      return `${videoCount} Videos, ${documentCount} Resources, ${quizCount} Quizzes`;
    };

    const computeModuleProgress = (module: Module): number => {
      if (!module.contents || module.contents.length === 0) return 0;
      const completedCount = module.contents.filter(content => content.completed).length;
      return Math.round((completedCount / module.contents.length) * 100);
    };

    const computeCourseProgress = (course: Course): number => {
      let total = 0;
      let completed = 0;
      course.modules.forEach((module) => {
        if (Array.isArray(module.contents)) {
          total += module.contents.length;
          completed += module.contents.filter(content => content.completed).length;
        }
      });
      return total > 0 ? Math.round((completed / total) * 100) : 0;
    };

    const fetchCourseDetails = async (): Promise<void> => {
      loading.value = true;
      try {
        const response = await axios.get<Course>(`course/detail/${props.id}`);
        course.value = response.data;
        for (const module of course.value.modules) {
          module.show = false;
        }
      } catch (err) {
        console.error(err);
        error.value = "An error occurred while fetching the subscription.";
      } finally {
        loading.value = false;
      }
    };

    const onContentComplete = async (moduleId: string, contentId: string): Promise<void> => {
      try {
        const response = await axios.post('/progress/complete', {
          courseId: course.value._id,
          moduleId,
          contentId,
        });
        console.log(response.data.message);
        // Find the module and content in the local course data.
        const mod = course.value.modules.find(mod => mod._id === moduleId);
        if (mod) {
          const content = mod.contents.find(cont => cont._id === contentId);
          if (content) {
            // Toggle the local “completed” flag based on the returned message.
            if (response.data.message === 'Content marked as complete') {
              content.completed = true;
            } else if (response.data.message === 'Content marked as incomplete') {
              content.completed = false;
            }
          }
        }
      } catch (err) {
        console.error('Error marking content as complete/incomplete:', err);
      }
    };

    onMounted(async () => {
      await fetchCourseDetails();
    });

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: course.value?.title,
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    return {
      router,
      settings,
      course,
      breadcrumbItems,
      loading,
      error,
      toggleModule,
      getContentCount,
      onContentComplete,
      computeModuleProgress,
      computeCourseProgress,
    };
  },
});
</script>
